import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../utils/api";
import { formatDateFR } from "../utils/format";

function getInterp(score) {
  if (score >= 90) return { label: "Excellent", desc: "Boutique conforme. Maintenir le niveau.", cls: "excellent" };
  if (score >= 80) return { label: "Bon", desc: "Quelques corrections mineures a suivre sous 7 jours.", cls: "bon" };
  if (score >= 65) return { label: "Moyen", desc: "Plan d'action obligatoire avec suivi sous 15 jours.", cls: "moyen" };
  if (score >= 50) return { label: "Insuffisant", desc: "Intervention Commercial / RH necessaire.", cls: "insuffisant" };
  return { label: "Critique", desc: "Action immediate avec Direction. Recontrole prioritaire.", cls: "critique" };
}

function getBarClass(pct) {
  if (pct >= 80) return "green";
  if (pct >= 55) return "orange";
  return "red";
}

function SectionScoreItem({ sec }) {
  const pct = sec.max_score > 0 ? Math.round((sec.obtained / sec.max_score) * 100) : 0;
  return (
    <div className="score-item">
      <div className="score-item-header">
        <div className="score-item-title">
          {sec.code}. {sec.title.split(" ")[0]}
          {!sec.editable && (
            <span style={{ marginLeft: 6, fontSize: 10, background: "#fef3c7", color: "#d97706", padding: "1px 6px", borderRadius: 8 }}>
              archive
            </span>
          )}
        </div>
        <div className="score-item-pts">
          {sec.obtained}
          <span style={{ fontSize: 13, fontWeight: 400, color: "var(--muted)" }}>/{sec.max_score}</span>
        </div>
      </div>
      <div className="score-bar">
        <div className={`score-bar-fill ${getBarClass(pct)}`} style={{ width: `${pct}%` }} />
      </div>
      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>{pct}%</div>
    </div>
  );
}

export default function SummaryPage() {
  const currentUser = api.getCurrentUser();
  const isDirector = currentUser?.role === "director";
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [visitStatus, setVisitStatus] = useState(null);
  const [conclusion, setConclusion] = useState({
    strengths: "", main_issues: "", decisions_on_site: "", support_needed: "", recheck_date: ""
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    api.getVisitDetail(id).then(d => {
      setVisitStatus(d.status);
      setDetail(d);
      setConclusion({
        strengths: d.strengths || "",
        main_issues: d.main_issues || "",
        decisions_on_site: d.decisions_on_site || "",
        support_needed: d.support_needed || "",
        recheck_date: d.recheck_date || "",
      });
    });
  }, [id]);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 2500); }
  function setC(k, v) { setConclusion(c => ({ ...c, [k]: v })); }

  async function finish() {
    setSaving(true);
    try {
      await api.updateVisit(id, { ...conclusion, status: "completed" });
      showToast("Visite cloturee !");
      setTimeout(() => navigate("/visits"), 1000);
    } finally { setSaving(false); }
  }

  async function saveConclusion() {
    setSaving(true);
    try {
      await api.updateVisit(id, conclusion);
      showToast("Sauvegarde");
    } finally { setSaving(false); }
  }

  async function exportJSON() {
    const data = await api.exportJSON(id);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `visite_${id}_${detail.store_name.replace(/\s/g, "_")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!detail) return (
    <div className="empty-state">
      <div className="empty-state-icon">o</div>
      <h3>Chargement...</h3>
    </div>
  );

  const interp = getInterp(detail.total_score);
  const allSections = [...detail.active_sections, ...detail.archived_sections];

  return (
    <div>
      <div className="page-header-row page-header">
        <div>
          <h1>Recapitulatif</h1>
          <p>
            {detail.store_name}
            {detail.visit_date ? " · " + formatDateFR(detail.visit_date) : ""}
            {detail.inspector ? " · " + detail.inspector : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-outline btn-sm" onClick={exportJSON}>⬇️ JSON</button>
          <a className="btn btn-outline btn-sm" href={api.getPdfUrl(id)} target="_blank" rel="noreferrer">📄 PDF</a>
          <button className="btn btn-outline btn-sm" onClick={() => navigate(`/visits/${id}/checklist`)}>✏️ Modifier</button>
        </div>
      </div>

      {/* Score band */}
      <div className={`interp-band ${interp.cls}`} style={{ marginBottom: 24 }}>
        <div className="interp-score">{detail.total_score}</div>
        <div style={{ borderLeft: "3px solid currentColor", paddingLeft: 16 }}>
          <div className="interp-label">{interp.label}</div>
          <div className="interp-desc">/{detail.max_total} pts · {interp.desc}</div>
        </div>
      </div>

      {/* Section scores - built from snapshots, shows ALL sections */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 16, color: "var(--navy)" }}>
          Scores par rubrique
          {detail.archived_sections.length > 0 && (
            <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 400, color: "var(--orange)" }}>
              (dont {detail.archived_sections.length} rubrique(s) archivee(s))
            </span>
          )}
        </div>
        <div className="score-summary">
          {allSections.map((sec, i) => (
            <SectionScoreItem key={`${sec.code}-${i}`} sec={sec} />
          ))}
        </div>
      </div>

      {/* Archived sections detail — show read-only scores */}
      {detail.archived_sections.length > 0 && (
        <div className="card" style={{ marginBottom: 20, borderLeft: "4px solid var(--orange)" }}>
          <div style={{ fontWeight: 700, marginBottom: 12, color: "var(--orange)", fontSize: 13 }}>
            Rubriques archivees — donnees de cette visite
          </div>
          <p style={{ fontSize: 12, color: "var(--muted)", marginBottom: 16 }}>
            Ces rubriques ont ete modifiees ou supprimees depuis cette visite.
            Les donnees sont conservees integralement.
          </p>
          {detail.archived_sections.map((sec, si) => (
            <div key={si} style={{ marginBottom: 16 }}>
              <div style={{
                background: "var(--navy)", color: "white", padding: "8px 14px",
                borderRadius: "6px 6px 0 0", fontSize: 13, fontWeight: 700,
                display: "flex", justifyContent: "space-between"
              }}>
                <span>{sec.code}. {sec.title}</span>
                <span style={{ color: "var(--accent)" }}>{sec.obtained}/{sec.max_score} pts</span>
              </div>
              {sec.checkpoints.map((cp, ci) => {
                const scoreColor = cp.score === 2 ? "var(--green)" : cp.score === 1 ? "var(--orange)" : "var(--red)";
                return (
                  <div key={ci} style={{
                    display: "grid", gridTemplateColumns: "1fr 50px 1fr",
                    gap: 12, padding: "10px 14px",
                    background: ci % 2 === 0 ? "white" : "var(--bg)",
                    borderBottom: "1px solid var(--border)"
                  }}>
                    <div style={{ fontSize: 13 }}>{cp.description}</div>
                    <div style={{ fontWeight: 700, color: scoreColor, textAlign: "center" }}>
                      {cp.score !== null ? cp.score : "-"}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>{cp.comment || ""}</div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Interpretation table */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 12, color: "var(--navy)" }}>Bareme</div>
        <table className="data-table">
          <thead><tr><th>Score</th><th>Evaluation</th><th>Decision recommandee</th></tr></thead>
          <tbody>
            {[
              [">= 90", "Excellent", "Maintenir le niveau."],
              ["80-89", "Bon", "Corrections mineures sous 7 jours."],
              ["65-79", "Moyen", "Plan d'action sous 15 jours."],
              ["50-64", "Insuffisant", "Intervention Commercial / RH."],
              ["< 50", "Critique", "Action immediate avec Direction."],
            ].map(([s, e, d]) => (
              <tr key={s}>
                <td style={{ fontFamily: "var(--mono)" }}>{s}</td>
                <td>{e}</td>
                <td>{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Conclusion */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 16, color: "var(--navy)" }}>Conclusion</div>
        <div className="form-grid">
          <div className="form-group full">
            <label>Points forts constates</label>
            <textarea value={conclusion.strengths} onChange={e => setC("strengths", e.target.value)} rows={2} />
          </div>
          <div className="form-group full">
            <label>Principales anomalies</label>
            <textarea value={conclusion.main_issues} onChange={e => setC("main_issues", e.target.value)} rows={2} />
          </div>
          <div className="form-group full">
            <label>Decisions prises sur place</label>
            <textarea value={conclusion.decisions_on_site} onChange={e => setC("decisions_on_site", e.target.value)} rows={2} />
          </div>
          <div className="form-group full">
            <label>Support necessaire</label>
            <textarea value={conclusion.support_needed} onChange={e => setC("support_needed", e.target.value)} rows={2} />
          </div>
          <div className="form-group">
            <label>Date de recontrole</label>
            <input type="date" value={conclusion.recheck_date} onChange={e => setC("recheck_date", e.target.value)} />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          {visitStatus === "validated" && (
          <span style={{ background: "#dcfce7", color: "var(--green)", border: "1px solid var(--green)", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 700 }}>
            ✅ Visite validee
          </span>
        )}
        <button className="btn btn-outline" onClick={saveConclusion} disabled={saving || (visitStatus === "validated" && !isDirector)}>
            {saving ? "..." : "💾 Sauvegarder"}
          </button>
        </div>
      </div>

      <div className="step-actions">
        <button className="btn btn-outline" onClick={() => navigate(`/visits/${id}/actions`)}>Actions</button>
        <button className="btn btn-accent" onClick={finish} disabled={saving}>
          {saving ? "..." : "✅ Cloturer la visite"}
        </button>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
